import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Folder,
  FolderOpen,
  Bot,
  Cpu,
  Database,
  FileText,
  MessagesSquare,
  FolderPlus,
  Sparkles,
  Library,
  Users,
} from 'lucide-react';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { useAnalytics } from '@cotera/client/app/etc';
import { Icon } from '@cotera/client/app/components/ui';
import {
  ModelScopeFactory,
  useAction,
  useActionsContext,
  useInScopeModel,
  useWatchableValue,
} from '@cotera/client/v0/actions/framework';
import { CocoEvent, useTracking } from '@cotera/client/app/etc';
import { CreateFolderAction } from '../resources/actions/create-folder.action';
import { useResourceNavigator } from '../resources/navigation';
import { NewChatAction } from '../chat/actions/new-chat.action';
import { coco } from '@cotera/sdk/core';
import { ORGANIZE_ARTIFACT_TYPE, organizeArtifactId } from '@cotera/api';
import {
  organizeDraftQueryKey,
  openOrganizeDraft,
} from './library-organize-draft-panel';
import { NewAgentAction } from '../agent/new-agent.action';
import { AddDatasetAction } from '../dataset/add-dataset.action';
import { OpenAgentChatAction } from '../chat/actions/open-agent-chat.action';
import { NewDocumentAction } from '../document/new-document.action';
import {
  ResourceListView,
  type Column,
  type ItemAction,
} from '../resources/list/resource-list-view';
import {
  ResourceMenuItem,
  shareMoveArchiveItems,
} from '../resources/list/resource-item-actions';
import {
  FavoriteToggleButton,
  useFavoriteMenuItem,
} from '../resources/favorites';
import { Button } from '@cotera/client/v0/components/ui/button';
import { useAuth } from '@workos-inc/authkit-react';
import { useFeatureFlagEnabled } from '@cotera/client/app/hooks/use-feature-flag';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@cotera/client/v0/components/ui/dropdown-menu';
import { useUrlAdapter } from '@cotera/client/app/etc/url-param-watchable';
import {
  FolderContentsPageModel,
  useFolderContentsPageModel,
  getFolderContentItemId,
  type FolderContentItem,
  type FolderContentsData,
  type LibraryViewMode,
} from './folder-contents-page.model';
import { getItemDisplayName } from './folder-contents-item-display';
import {
  buildExpandedChildrenListItems,
  buildFolderNestedListItems,
  isVirtualChatsItem,
  type FolderContentsListItem,
} from './folder-contents-list-items';
import {
  folderContentsQueryKey,
  folderViewItemCount,
  resolveDisplayedTotal,
  type PinnedTotal,
} from './folder-item-count';
import { useWorkflowSummaries } from '../workflow/use-workflow-summaries';
import type { SortOption } from '../resources/list/resource-list-view';
import { RESOURCE_LIST_COLUMNS } from '../resources/list/resource-list-types';
import {
  ResourceSelectionModel,
  ResourceType,
} from '../resources/selection/resource-selection.model';
import {
  RESOURCE_TYPE_CONFIG,
  type NonFolderResourceType,
} from '../resources/type/resource-type-config';
import { ResourceTypeIconBubble } from '../resources/type/resource-type-icon-bubble';
import { MoveToFolderAction } from '../resources/actions/move-to-folder.action';
import {
  moveResourcesToFolder,
  type MovableResource,
} from '../resources/actions/move-resources-to-folder';
import { ArchiveResourceAction } from '../resources/actions/archive-resource.action';
import { ArchiveFolderAction } from './archive-folder.action';
import { RenameFolderAction } from '../resources/actions/rename-folder.action';
import { ShareResourceLinkAction } from '../resources/actions/share-resource-link.action';
import {
  ResourcePageShell,
  type BreadcrumbItem,
} from '../resources/resource-page-shell';
import { useWatchablesFactory } from '../watchables/watchables-factory-context';
import { LibraryRow } from './library-list-row';
import { LibraryCard } from './library-card';
import {
  flattenLibraryTree,
  hiddenChildCounts,
  toggleExpanded,
  type LibraryTreeMeta,
} from './folder-tree-items';
import { useFolderChildren } from './use-folder-children';
import { buildFolderTrailCrumbs } from './folder-breadcrumbs';
import {
  LibraryFilters,
  LibraryFiltersMenu,
  isOwnershipFilter,
  isTypeFilter,
  type OwnershipFilter,
  type TypeFilter,
} from './library-filters';
import { LibraryViewToggle } from './library-view-toggle';
import { useFolderAncestors } from './use-folder-ancestors';
import { useIsBelowSm } from '@cotera/client/app/hooks/use-breakpoint';
import { useHostOwnsSharing } from '@cotera/client/v0/chat/artifact-panel-context';

/** Folder list item kinds, as the `ResourceType` the navigator speaks. */
const LIST_ITEM_RESOURCE_TYPE = {
  folder: ResourceType.Folder,
  chat: ResourceType.Chat,
  document: ResourceType.Document,
  workflow: ResourceType.Workflow,
  dataset: ResourceType.Dataset,
  workflow_trigger: ResourceType.WorkflowTrigger,
} as const;

/**
 * When folder contents render outside `/…/folders/:folderId` (e.g. chat artifact panel),
 * wrap the tree with `FolderArtifactFolderIdProvider` so hooks can resolve the folder id
 * without nesting another Router (React Router forbids that).
 */
const FolderArtifactFolderIdContext = createContext<string | undefined>(
  undefined
);

export const FolderArtifactFolderIdProvider: React.FC<{
  folderId: string;
  children: React.ReactNode;
}> = ({ folderId, children }) => (
  <FolderArtifactFolderIdContext.Provider value={folderId}>
    {children}
  </FolderArtifactFolderIdContext.Provider>
);

function useFolderPageRouteFolderId(): string | undefined {
  const fromArtifact = useContext(FolderArtifactFolderIdContext);
  const { folderId: fromParams } = useParams<{ folderId?: string }>();
  if (fromArtifact !== undefined) {
    return fromArtifact;
  }
  return fromParams;
}

function getListItemId(item: FolderContentsListItem): string {
  if (isVirtualChatsItem(item)) {
    return item.data.id;
  }
  return getFolderContentItemId(item);
}

function getListItemDisplayName(item: FolderContentsListItem): string {
  if (isVirtualChatsItem(item)) {
    return item.data.title;
  }
  return getItemDisplayName(item);
}

function compareByDate(
  a: FolderContentsListItem,
  b: FolderContentsListItem,
  field: 'updatedAt' | 'createdAt'
): number {
  const read = (item: FolderContentsListItem): number => {
    // Annotated rather than asserted: every list-item payload carries both
    // timestamps, so this narrows structurally without a cast.
    const data: { updatedAt?: string | Date; createdAt?: string | Date } =
      item.data;
    const raw = data[field];
    return raw === undefined ? 0 : new Date(raw).getTime();
  };
  return read(b) - read(a);
}

const SORT_UPDATED: SortOption<FolderContentsListItem> = {
  label: 'Updated',
  key: 'updatedAt',
  compareFn: (a, b) => compareByDate(a, b, 'updatedAt'),
};

const SORT_CREATED: SortOption<FolderContentsListItem> = {
  label: 'Created',
  key: 'createdAt',
  compareFn: (a, b) => compareByDate(a, b, 'createdAt'),
};

const SORT_NAME: SortOption<FolderContentsListItem> = {
  label: 'Name',
  key: 'name',
  compareFn: (a, b) =>
    getListItemDisplayName(a).localeCompare(getListItemDisplayName(b)),
};

/**
 * Sort options valid for the current view.
 *
 * Sorting is server-side, and the single-type views go through `resource.list`,
 * whose per-type allow-lists (`AGENT_SORT_FIELDS`, `DOCUMENT_SORT_FIELDS`) have
 * no `name` — a name sort there is silently discarded and the list comes back
 * ordered by `updatedAt`. Rather than show a control that does nothing, drop it
 * for those views.
 */
function sortOptionsForType(
  type: TypeFilter
): SortOption<FolderContentsListItem>[] {
  const supportsName = type !== 'agent' && type !== 'document';
  return supportsName
    ? [SORT_UPDATED, SORT_CREATED, SORT_NAME]
    : [SORT_UPDATED, SORT_CREATED];
}

/**
 * Resource kinds offered by the "New" menu, in creation-frequency order.
 *
 * Bare nouns. These led with a verb to avoid repeating the button's own "New",
 * which is the right instinct applied to the wrong word: four of the six verbs
 * are "Create", so the leading word ended up being the thing the rows *don't*
 * differ by, and the noun that distinguishes them landed last.
 *
 * No trigger here. A trigger is the one thing this menu could create that the
 * Library never lists back — the rows are chats, agents, documents, datasets,
 * workflows and folders — so creating one from here made it disappear. Triggers
 * are created from Automations, which is where they show up. (The wizard itself
 * still takes a `folderId`, for the workflow it creates.)
 */
const ADD_RESOURCE_OPTIONS = [
  {
    type: 'chat' as const,
    label: 'Chat',
    icon: MessagesSquare,
    resourceType: ResourceType.Chat,
  },
  {
    type: 'agent' as const,
    label: 'Agent',
    icon: Bot,
    resourceType: ResourceType.Agent,
  },
  {
    type: 'document' as const,
    label: 'Document',
    icon: FileText,
    resourceType: ResourceType.Document,
  },
  {
    type: 'dataset' as const,
    label: 'Dataset',
    icon: Database,
    resourceType: ResourceType.Dataset,
  },
  {
    type: 'folder' as const,
    label: 'Folder',
    icon: FolderPlus,
    resourceType: ResourceType.Folder,
  },
] as const;

type AddResourceType = (typeof ADD_RESOURCE_OPTIONS)[number]['type'];

const FolderContentsListContent: React.FC = () => {
  const folderId = useFolderPageRouteFolderId();
  const [searchParams] = useSearchParams();
  const org = useWhoami((s) => s.org);
  const userId = useWhoami((s) => s.whoami.id);
  useTracking(CocoEvent.FolderViewed, {
    folderId: folderId ?? null,
    orgId: org.id,
  });
  const navigate = useNavigate();
  const resourceNavigator = useResourceNavigator();
  const listPageModel = useFolderContentsPageModel();
  const createFolder = useAction(CreateFolderAction);
  const newChatAction = useAction(NewChatAction);
  const newAgentAction = useAction(NewAgentAction);
  const openAgentChatAction = useAction(OpenAgentChatAction);
  const newDocumentAction = useAction(NewDocumentAction);
  const addDatasetAction = useAction(AddDatasetAction);
  const moveToFolderAction = useAction(MoveToFolderAction);
  const archiveResourceAction = useAction(ArchiveResourceAction);
  const archiveFolderAction = useAction(ArchiveFolderAction);
  const renameFolderAction = useAction(RenameFolderAction);
  const shareAction = useAction(ShareResourceLinkAction);
  const actionsContext = useActionsContext();
  const selectionModel = useInScopeModel(ResourceSelectionModel);
  const client = useTenantedClient();
  const queryClient = useQueryClient();
  const analytics = useAnalytics();

  useEffect(() => {
    listPageModel.syncFromUrl(searchParams);
  }, [listPageModel, searchParams]);

  const listParams = useWatchableValue(listPageModel.listParamsWatchable);
  const { page, limit, search, sort, sortDir, scope, type, ownership, view } =
    listParams;
  const folderContentsData = useWatchableValue(
    listPageModel.folderContentsDataWatchable
  );
  const optimisticallyRemovedIds = useWatchableValue(
    listPageModel.optimisticallyRemovedIdsWatchable
  );
  const loadError = useWatchableValue(listPageModel.loadErrorWatchable);
  const isFetching = useWatchableValue(listPageModel.isFetchingWatchable);

  const displayData = useMemo((): FolderContentsData | null => {
    if (folderContentsData === null) {
      return null;
    }
    if (optimisticallyRemovedIds.size === 0) {
      return folderContentsData;
    }
    const filteredItems = folderContentsData.items.filter(
      (item) => !optimisticallyRemovedIds.has(getFolderContentItemId(item))
    );
    return {
      ...folderContentsData,
      items: filteredItems,
      total: Math.max(
        0,
        folderContentsData.total -
          (folderContentsData.items.length - filteredItems.length)
      ),
    };
  }, [folderContentsData, optimisticallyRemovedIds]);

  const isTopLevel = listPageModel.isTopLevel;
  const hostOwnsSharing = useHostOwnsSharing();
  const rawItems = useMemo(() => displayData?.items ?? [], [displayData]);
  // Last-run status for workflow rows (batched), shown inline in the row
  // subheading so workflows carry run health without breaking the unified layout.
  const workflowIdsInView = useMemo(
    () =>
      rawItems
        .filter((item) => item.type === 'workflow')
        .map((item) => item.data.id),
    [rawItems]
  );
  const { summaries: workflowSummaries } = useWorkflowSummaries(
    workflowIdsInView,
    workflowIdsInView.length > 0
  );
  // The Library filters are SERVER-side (ownership/type/search/sort/paging are
  // resolved by `getLibraryRoot` / `resource.list` / `getFolderItems`). The page
  // just drives the controls; `rawItems` is already the filtered, sorted,
  // paginated page — folders float to the top in the "All" view because the
  // server orders them first (and `ResourceListView` keeps that order since sort
  // is server-controlled).
  const typeFilter: TypeFilter = type;
  const ownershipFilter: OwnershipFilter = ownership;
  const topLevelItems = rawItems;
  const nestedFolderItems = useMemo(
    (): FolderContentsListItem[] =>
      buildFolderNestedListItems({
        isTopLevel,
        folderId,
        rawItems,
        chatSummary: displayData?.chatSummary ?? null,
      }),
    [isTopLevel, folderId, rawItems, displayData]
  );
  const total = displayData?.total ?? 0;
  const folderMeta = displayData?.folderMeta ?? null;
  const hasMore = total > rawItems.length;

  // The headline count is the rows this view lists — the same rule the folder
  // rows in the Library follow, so a folder reads the same from outside as it
  // does from inside. See {@link folderViewItemCount}.
  const displayedItemCount = folderViewItemCount({
    total,
    showsChatsRow: nestedFolderItems.some(isVirtualChatsItem),
  });

  // Inline folder expansion. Only in `All` + list mode: a type filter already
  // flattens across folders (so there is no hierarchy left to expand), and a
  // card grid has no sensible place to put nested rows.
  const treeEnabled = typeFilter === 'all' && view === 'list';
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  const expandedFolderIds = useMemo(
    () => (treeEnabled ? [...expandedIds] : []),
    [treeEnabled, expandedIds]
  );
  const {
    childrenByFolderId,
    childTotalsByFolderId,
    chatSummaryByFolderId,
    loadingFolderIds,
  } = useFolderChildren(
    expandedFolderIds,
    typeFilter,
    scope,
    sort,
    sortDir,
    search
  );

  // Expanding fetches one page per folder, so a bigger folder discloses only a
  // prefix. The remainder goes on the folder's row rather than leaving the rows
  // underneath silently short of the "N items" the same row advertises.
  const hiddenChildCountByFolderId = useMemo(
    () =>
      hiddenChildCounts({
        expandedIds,
        childrenByFolderId,
        childTotalsByFolderId,
      }),
    [expandedIds, childrenByFolderId, childTotalsByFolderId]
  );

  const handleToggleExpand = useCallback((item: FolderContentsListItem) => {
    if (isVirtualChatsItem(item) || item.type !== 'folder') {
      return;
    }
    const id = getFolderContentItemId(item);
    setExpandedIds((prev) => toggleExpanded(prev, id));
  }, []);

  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const handleLoadMore = useCallback(() => {
    listPageModel.loadPage(page + 1, limit);
    setIsLoadingMore(true);
  }, [listPageModel, page, limit]);

  // Clear loading-more state when fetch completes so the sentinel can fire again.
  // Without this, the IntersectionObserver would fire loadPage repeatedly during fetch,
  // causing cascading redundant fetches (model fetches pages 1..N on each run).
  useEffect(() => {
    if (displayData !== null && rawItems.length > 0) {
      setIsLoadingMore(false);
    }
  }, [displayData, rawItems.length]);

  // The box renders the draft; the model debounces the commit (fetch + URL
  // rewrite) and owns adopting external changes (URL restore, clear-filters).
  const searchDraft = useWatchableValue(listPageModel.searchDraftWatchable);

  const handleSearchChange = useCallback(
    (value: string) => {
      listPageModel.setSearchDraft(value);
    },
    [listPageModel]
  );

  const handleSortChange = useCallback(
    (key: string, direction: 'asc' | 'desc') => {
      listPageModel.setSort(key, direction);
    },
    [listPageModel]
  );

  // Search is resolved server-side for every view, so the client filter only
  // needs to avoid re-filtering results the server already matched (it can match
  // on fields the row doesn't render, e.g. a workflow's bound trigger name).
  const filterFn = useCallback(() => true, []);

  const handleClickItem = useCallback(
    (item: FolderContentsListItem) => {
      switch (item.type) {
        case 'virtual-chats': {
          // The row's own folder, not the page's: an expanded subfolder's Chats
          // row belongs to that subfolder.
          navigate(`/${org.slug}/chats?folderId=${item.data.folderId}`);
          return;
        }
        case 'agent': {
          void openAgentChatAction.execute({
            agentId: item.data.id,
            ...(folderId !== undefined && { folderId }),
          });
          return;
        }
        // Everything else goes through the navigator, so clicking an item in a
        // folder that's itself open as an artifact shows it as a sibling
        // artifact instead of routing the whole app away.
        case 'folder':
        case 'chat':
        case 'document':
        case 'workflow':
        case 'dataset':
        case 'workflow_trigger': {
          resourceNavigator.open({
            resourceType: LIST_ITEM_RESOURCE_TYPE[item.type],
            resourceId: getFolderContentItemId(item),
          });
          return;
        }
      }
    },
    [folderId, navigate, org.slug, openAgentChatAction, resourceNavigator]
  );

  const setResourceSelection = useCallback(
    (items: FolderContentsListItem[]) => {
      if (actionsContext.isInScope(ResourceSelectionModel) === true) {
        actionsContext.removeTargetForType(ResourceSelectionModel);
      }

      const selectableItems = items.filter(
        (item): item is FolderContentItem => !isVirtualChatsItem(item)
      );

      if (selectableItems.length === 0) {
        selectionModel.clearSelections();
        return;
      }

      selectionModel.setSelections(
        selectableItems.map((item) => ({
          resourceType: item.type as ResourceType,
          resourceId: (item.data as { id: string }).id,
          name: getItemDisplayName(item),
        }))
      );
      actionsContext.targetModel(selectionModel);
    },
    [actionsContext, selectionModel]
  );

  const handleFocusItems = useCallback(
    (focusedItems: FolderContentsListItem[]) => {
      setResourceSelection(focusedItems);
    },
    [setResourceSelection]
  );

  const handleOpenActionMenu = useCallback(
    (item: FolderContentsListItem) => {
      if (isVirtualChatsItem(item)) {
        return;
      }
      setResourceSelection([item]);
    },
    [setResourceSelection]
  );

  // Only real (non-virtual) rows can be dragged; only folder rows accept drops.
  const canDragLibraryItem = useCallback(
    (item: FolderContentsListItem) => !isVirtualChatsItem(item),
    []
  );

  const canDropOnFolder = useCallback(
    (item: FolderContentsListItem) =>
      !isVirtualChatsItem(item) && item.type === 'folder',
    []
  );

  // One subscription to the favorites state for the whole list — the row menus
  // are built inside a `map`, where a per-row hook is not available.
  const favoriteMenuItem = useFavoriteMenuItem();

  // Drag-and-drop move: drop the dragged rows (the multi-selection, or a single
  // grabbed row) onto a folder to move them in. Reuses the same move mechanics
  // as the "Move to folder" action but skips its modal picker — the drop target
  // IS the destination.
  const handleDropOnFolder = useCallback(
    (
      target: FolderContentsListItem,
      draggedItems: FolderContentsListItem[]
    ) => {
      // Narrow to a real folder row. `canDropOnFolder` isn't a TS type guard, so
      // repeat the checks here to satisfy the type of getFolderContentItemId.
      if (isVirtualChatsItem(target) || target.type !== 'folder') {
        return;
      }
      const targetFolderId = getFolderContentItemId(target);
      const resources: MovableResource[] = draggedItems
        .filter((item): item is FolderContentItem => !isVirtualChatsItem(item))
        // Never move a folder into itself.
        .filter((item) => getFolderContentItemId(item) !== targetFolderId)
        .map((item) => ({
          type: item.type as ResourceType,
          id: (item.data as { id: string }).id,
        }));
      if (resources.length === 0) {
        return;
      }
      void moveResourcesToFolder({
        client,
        queryClient,
        orgId: org.id,
        resources,
        folderId: targetFolderId,
        optimisticIds: resources.map((r) => r.id),
        folderPageModel: listPageModel,
      }).then((result) => {
        if (result.isErr()) {
          return;
        }
        // The list clears its own multi-select on drop (which also clears the
        // ResourceSelectionModel via onFocus), so we only track here.
        analytics.track(CocoEvent.ResourceMovedToFolder, {
          destinationFolderId: targetFolderId,
          resourceIds: resources.map((r) => r.id),
          resourceTypes: [...new Set(resources.map((r) => r.type))],
          count: resources.length,
          via: 'drag-and-drop',
        });
      });
    },
    [client, queryClient, org.id, listPageModel, analytics]
  );

  const getItemActions = useCallback(
    (item: FolderContentsListItem): ItemAction[] => {
      if (isVirtualChatsItem(item)) {
        // The collapsed "Chats" row is a summary of many chats, not a resource
        // — there is nothing here for a star to point at.
        return [ResourceMenuItem.open(() => handleClickItem(item))];
      }
      if (item.type === 'folder') {
        return [
          // Favorite leads every one of these menus. It is the only entry that
          // changes nothing for anyone else — no grant moves, nothing is
          // renamed or filed — so it reads first, above the actions that do,
          // and the destructive one stays furthest from it. Notion and Linear
          // both open their row menus this way.
          favoriteMenuItem(ResourceType.Folder, item.data.id),
          // A folder opens *into* itself, so it takes the opening-folder glyph
          // rather than the leave-this-page arrow the resource rows use.
          ResourceMenuItem.openFolder(() => handleClickItem(item)),
          ResourceMenuItem.rename(
            () => void renameFolderAction.execute({ name: item.data.name }),
            'Rename folder'
          ),
          // Folders archive through their own action: it warns about what is
          // inside, which the plain resource archive says nothing about.
          ...shareMoveArchiveItems({
            onShare: () => void shareAction.execute({}),
            onMoveToFolder: () => void moveToFolderAction.execute({}),
            onArchive: () => void archiveFolderAction.execute({}),
          }),
        ];
      }
      return [
        favoriteMenuItem(item.type as ResourceType, item.data.id),
        ResourceMenuItem.open(() => handleClickItem(item)),
        ...shareMoveArchiveItems({
          onShare: () => void shareAction.execute({}),
          onMoveToFolder: () => void moveToFolderAction.execute({}),
          onArchive: () => void archiveResourceAction.execute({}),
        }),
      ];
    },
    [
      handleClickItem,
      favoriteMenuItem,
      shareAction,
      moveToFolderAction,
      archiveResourceAction,
      archiveFolderAction,
      renameFolderAction,
    ]
  );

  const renderIcon = useCallback((item: FolderContentsListItem) => {
    if (isVirtualChatsItem(item)) {
      return (
        <ResourceTypeIconBubble
          resourceType={ResourceType.Chat}
          icon={RESOURCE_TYPE_CONFIG[ResourceType.Chat].icon}
        />
      );
    }
    if (item.type === 'folder') {
      return (
        <ResourceTypeIconBubble
          resourceType={ResourceType.Folder}
          icon={Folder}
        />
      );
    }
    const resourceType = item.type as NonFolderResourceType;
    const icon = RESOURCE_TYPE_CONFIG[resourceType]?.icon ?? Cpu;
    // The bubble color still signals "agent" when a custom emoji replaces the icon.
    const emoji = item.type === 'agent' ? item.data.emoji : null;
    return (
      <ResourceTypeIconBubble
        resourceType={resourceType}
        icon={icon}
        emoji={emoji}
      />
    );
  }, []);

  // Google-Drive-style drag card: the grabbed item's icon + name under the
  // cursor, with a count badge when a multi-selection is dragged.
  const renderDragCard = useCallback(
    (items: FolderContentsListItem[]) => {
      const first = items[0];
      if (first === undefined) {
        return null;
      }
      return (
        <div className="inline-flex max-w-[260px] items-center gap-2 rounded-lg border border-border bg-popover px-3 py-2 shadow-lg">
          <span className="flex h-5 w-5 shrink-0 items-center justify-center">
            {renderIcon(first)}
          </span>
          <span className="truncate text-sm font-medium text-foreground">
            {getListItemDisplayName(first)}
          </span>
          {items.length > 1 ? (
            <span className="ml-1 inline-flex h-5 min-w-[1.25rem] shrink-0 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-semibold text-primary-foreground">
              {items.length}
            </span>
          ) : null}
        </div>
      );
    },
    [renderIcon]
  );

  const nestedFolderScope = !isTopLevel ? folderMeta?.scope ?? null : null;

  // The date column follows the active sort, so the shown value always explains
  // the order (and is a neutral reference when sorting by name).
  const dateField = sort === 'createdAt' ? 'createdAt' : 'updatedAt';
  const dateColumnLabel = dateField === 'createdAt' ? 'Created' : 'Updated';

  const renderItem = useCallback(
    (item: FolderContentsListItem) => (
      <LibraryRow
        item={item}
        userId={userId}
        nestedFolderScope={nestedFolderScope}
        isTopLevel={isTopLevel}
        workflowSummaries={workflowSummaries}
        dateField={dateField}
        hiddenChildCount={hiddenChildCountByFolderId.get(getListItemId(item))}
      />
    ),
    [
      nestedFolderScope,
      dateField,
      userId,
      workflowSummaries,
      isTopLevel,
      hiddenChildCountByFolderId,
    ]
  );

  const renderCard = useCallback(
    (item: FolderContentsListItem) => (
      <LibraryCard
        item={item}
        userId={userId}
        nestedFolderScope={nestedFolderScope}
        isTopLevel={isTopLevel}
        workflowSummaries={workflowSummaries}
        dateField={dateField}
        icon={renderIcon(item)}
      />
    ),
    [
      nestedFolderScope,
      dateField,
      userId,
      workflowSummaries,
      isTopLevel,
      renderIcon,
    ]
  );

  const title = isTopLevel ? 'Library' : folderMeta?.name ?? 'Folder';

  // `displayData === null` means nothing has ever loaded. A failed refresh keeps
  // the previous page on screen, so an error only takes over the view when there
  // is nothing to fall back to.
  const hasLoadedOnce = displayData !== null;
  const status: 'loading' | 'ready' | 'error' =
    loadError !== null && !hasLoadedOnce
      ? 'error'
      : hasLoadedOnce
      ? 'ready'
      : 'loading';
  const titleLoading = !isTopLevel && status === 'loading';

  // The count under the title is pinned to the query, not to the most recent
  // page.
  //
  // "Load more" refetches pages 1..N and re-reads `total` from the last of them,
  // so the headline number was being re-derived on every scroll. The server does
  // return the full filtered size on each page, so it should land on the same
  // value — but a number this prominent shouldn't be recomputed from whatever a
  // page happened to report. Capturing it once per query means scrolling cannot
  // move it, whatever the pages say.
  //
  // The key excludes `page` and `sort` (so paging and reordering never refresh
  // it) and includes the optimistic-removal count (so archiving or moving an
  // item still updates it).
  //
  // A total may only be pinned when the data it was read from answers this
  // query — `displayData.query` says which one it answered, and a filter change
  // lands a render before its fetch does. See {@link resolveDisplayedTotal}.
  const queryKey = folderContentsQueryKey({
    folderId: folderId ?? null,
    type,
    ownership,
    search,
    scope: scope ?? null,
  });
  const countKey = [queryKey, optimisticallyRemovedIds.size].join('|');
  const pinnedTotalRef = useRef<PinnedTotal | null>(null);
  const { pinned, total: pinnedTotal } = resolveDisplayedTotal({
    previous: pinnedTotalRef.current,
    key: countKey,
    count: displayedItemCount,
    canPin:
      displayData !== null &&
      folderContentsQueryKey(displayData.query) === queryKey,
  });
  pinnedTotalRef.current = pinned;

  const { ancestors, isLoading: ancestorsLoading } = useFolderAncestors(
    isTopLevel ? null : folderId
  );

  const breadcrumbs = useMemo((): BreadcrumbItem[] | undefined => {
    if (isTopLevel) {
      return [];
    }
    // `folderId` is always set off the top level (the model derives `isTopLevel`
    // from its absence); narrowed rather than defaulted so no crumb can point at
    // an empty id.
    if (folderId === undefined || !hasLoadedOnce || ancestorsLoading) {
      return undefined;
    }
    return buildFolderTrailCrumbs({
      orgSlug: org.slug,
      // The contents payload already carries this folder's name and scope, so
      // the trail doesn't wait on the ancestor walk's copy of them.
      folder: {
        id: folderId,
        name: folderMeta?.name ?? 'Folder',
        scope: folderMeta?.scope ?? null,
      },
      ancestors,
    });
  }, [
    isTopLevel,
    hasLoadedOnce,
    ancestorsLoading,
    ancestors,
    folderId,
    folderMeta,
    org.slug,
  ]);

  const folderListColumns = useMemo(
    (): Column[] => [
      // Named in the column table, silent in the stacked header — see
      // `Column.labelHidden`. No `sortKey`: click-to-sort hangs off the label,
      // and this one has no label in the narrow layout to hang it on. Sorting by
      // name is in the toolbar's sort menu, where the other two orders live.
      { columnKey: 'name', label: 'Name', labelHidden: true },
      // No Type column. The colour-coded icon tile already carries the type in
      // both views, and a text column repeating it spent 110px of a row that
      // has better uses. The rows/cards keep the label as screen-reader-only
      // text, since the tile itself has no accessible name.
      RESOURCE_LIST_COLUMNS.createdBy,
      RESOURCE_LIST_COLUMNS.sharedWith,
      {
        columnKey: 'date',
        label: dateColumnLabel,
        width: '100px',
        sortKey: dateField,
      },
    ],
    [dateColumnLabel, dateField]
  );

  const filtersActive =
    typeFilter !== 'all' ||
    (isTopLevel && ownershipFilter !== 'all') ||
    search !== '';

  const typeNoun = ((): string => {
    switch (typeFilter) {
      case 'agent':
        return 'agents';
      case 'document':
        return 'documents';
      case 'workflow':
        return 'workflows';
      case 'dataset':
        return 'datasets';
      case 'all':
        return 'items';
    }
  })();

  // The empty message names whatever is actually narrowing the view. Previously
  // a type-filtered empty result claimed "Your library is empty", which is a
  // different (and wrong) statement.
  const emptyText = ((): string => {
    if (typeFilter !== 'all') {
      return isTopLevel
        ? `No ${typeNoun} yet`
        : `No ${typeNoun} in this folder`;
    }
    if (!isTopLevel) {
      return 'This folder is empty';
    }
    if (ownershipFilter === 'shared') {
      return 'Nothing has been shared with you yet';
    }
    if (ownershipFilter === 'mine') {
      return "You don't own anything here yet";
    }
    return 'Your library is empty';
  })();

  const currentFolderId = folderId ?? undefined;

  const handleAddResource = useCallback(
    (addType: AddResourceType) => {
      if (addType === 'chat') {
        void newChatAction.execute({
          ...(currentFolderId !== undefined && { folderId: currentFolderId }),
        });
        return;
      }
      if (addType === 'agent') {
        void newAgentAction.execute({
          ...(currentFolderId !== undefined && { folderId: currentFolderId }),
        });
        return;
      }
      if (addType === 'document') {
        void newDocumentAction.execute({
          ...(currentFolderId !== undefined && { folderId: currentFolderId }),
        });
        return;
      }
      if (addType === 'dataset') {
        // The manual/CSV chooser owns the flow from here. It does not yet take a
        // target folder, so — unlike the options above — a dataset created here
        // lands at the top level rather than in the current folder.
        void addDatasetAction.execute({});
        return;
      }
      if (currentFolderId !== undefined) {
        void createFolder.execute({ parentId: currentFolderId });
        return;
      }
      void createFolder.execute({
        parentId: null,
        scope: scope === 'shared' ? 'org' : 'user',
      });
    },
    [
      currentFolderId,
      newChatAction,
      newAgentAction,
      newDocumentAction,
      addDatasetAction,
      createFolder,
      scope,
    ]
  );

  // A menu on the button, not a modal: every option was a single choice with no
  // configuration, so the old flow cost a dialog open, a radio pick and a
  // Continue click to do one thing.
  const primaryAction = useMemo(
    () => ({
      label: 'New',
      onClick: () => handleAddResource('chat'),
      // The same colour-coded tile the rows below use, so what you create is
      // recognisable before it exists — and a muted glyph per row was the one
      // place in the Library where a resource type had no colour.
      menuItems: ADD_RESOURCE_OPTIONS.map((opt) => ({
        label: opt.label,
        icon: (
          <ResourceTypeIconBubble
            resourceType={opt.resourceType}
            icon={opt.icon}
            className="h-6 w-6 rounded"
            // `size-`/`text-` prefixes deliberately: `DropdownMenuItem` styles
            // bare descendant svgs (`[&_svg:not([class*='size-'])]:size-4`, and
            // the same for `text-`), and those descendant selectors outrank a
            // plain utility — without the prefixes the glyph renders 16px and
            // muted grey, which is the branding this exists to keep.
            iconClassName="size-3.5 text-inherit"
          />
        ),
        onClick: () => handleAddResource(opt.type),
      })),
    }),
    [handleAddResource]
  );

  // Only org admins can organize the SHARED library; we key off the WorkOS role
  // (the same signal the settings User Management gate uses), not whoami roles.
  const { role: workosRole } = useAuth();
  const canOrganizeShared = workosRole === 'admin';
  // Gated behind the WorkOS `organize-library` feature flag (per-org). Off → the
  // Library's Organize button is hidden, matching how `datasets`/`legacy-app` gate.
  const organizeEnabled = useFeatureFlagEnabled('organize-library');

  // At the Library root, a single sparkle icon beside the "Library" title --
  // our branded indigo marks it as an AI-assisted utility. Clicking it opens a
  // Coco chat with the organize review already in the artifact panel: we
  // PREFETCH the proposals at click and seed the panel (seedOnly, no forced
  // Coco turn), so by the time the chat is on screen the suggested folders are
  // there to review/apply, and the chat is ready for normal conversation.
  const openOrganizeChat = useCallback(
    (mode: 'personal' | 'shared' = 'personal') => {
      void (async () => {
        // The server decides the conversation id: it reuses an open organize chat
        // for this library (so repeated clicks don't pile up empty chats) or mints
        // one, and persists the organize-library artifact onto the conversation so
        // the panel rehydrates from the snapshot on reload like any other artifact.
        // It does NOT cluster, so this stays fast; clustering runs in the prefetch.
        const opened = await client.resource.folder.openOrganize({ mode });
        if (opened.isErr()) {
          return;
        }
        const chatId = opened.value.conversationId;
        // Prefetch the draft NOW so the panel reads it on mount and skips its own
        // loading. `openOrganizeDraft` re-clusters when the reused chat's draft is
        // empty (resuming only an in-progress plan), so a stale empty draft from an
        // earlier open never sticks and reports "all organized".
        void queryClient.prefetchQuery({
          queryKey: organizeDraftQueryKey(chatId),
          queryFn: () => openOrganizeDraft(client, chatId, mode),
        });
        void newChatAction.execute({
          chatId,
          seedOnly: true,
          initialArtifacts: [
            {
              artifactType: ORGANIZE_ARTIFACT_TYPE,
              artifactId: organizeArtifactId(chatId),
              // Carry the conversation id to the panel so it keys the shared draft
              // exactly as Coco's tools do, and reads the prefetched draft. The
              // mode titles the panel by library, as the server does on reload.
              data: { conversationId: chatId, mode },
            },
          ],
          // Load the draft tools so Coco can see/stage/answer against the same
          // staged plan the review panel shows (shared per-conversation draft).
          initialToolKeys: [...coco.COCO_ORGANIZE_DRAFT_TOOL_KEYS],
        });
      })();
    },
    [queryClient, client, newChatAction]
  );

  const organizeButtonClassName =
    'shrink-0 text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700 dark:text-indigo-400 dark:hover:bg-indigo-950/40 dark:hover:text-indigo-300';
  // One Sparkles button, reused as the dropdown trigger (admins, no onClick —
  // Radix wires it) and the plain action (everyone else). The call site only
  // renders this when `isTopLevel`.
  const organizeSparkleButton = (onClick?: () => void) => (
    <Button
      variant="ghost"
      size="icon-sm"
      className={organizeButtonClassName}
      aria-label="Organize resources in folders"
      title="Organize resources into folders"
      {...(onClick !== undefined && { onClick })}
    >
      <Sparkles className="h-4 w-4" aria-hidden="true" />
    </Button>
  );
  const suggestFoldersAction = !organizeEnabled ? null : canOrganizeShared ? (
    // Admins pick which library to organize; everyone else just organizes theirs.
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {organizeSparkleButton()}
      </DropdownMenuTrigger>
      {/* Mirrors the library "New" menu: icon-led action rows, same spacing. */}
      <DropdownMenuContent align="start" className="min-w-[12rem] p-1">
        <DropdownMenuItem
          onClick={() => openOrganizeChat('personal')}
          className="cursor-pointer gap-2.5 px-2.5 py-2"
        >
          <Library
            className="h-4 w-4 text-muted-foreground"
            aria-hidden="true"
          />
          My library
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => openOrganizeChat('shared')}
          className="cursor-pointer gap-2.5 px-2.5 py-2"
        >
          <Users className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          Shared library
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  ) : (
    organizeSparkleButton(() => openOrganizeChat('personal'))
  );

  const folderShareAction = (() => {
    if (folderId === null || folderId === undefined || isTopLevel) {
      return null;
    }
    // In a chat's artifact panel the chat's Share already opens a dialog with
    // this folder in it as a selectable row, so drawing one here is the same
    // dialog twice. See `useHostOwnsSharing`.
    if (hostOwnsSharing) {
      return null;
    }
    const targetFolderId = folderId;
    return (
      <Button
        variant="default"
        size="sm"
        aria-label="Share folder"
        onClick={() =>
          void shareAction.execute({
            targets: [{ type: ResourceType.Folder, id: targetFolderId }],
          })
        }
      >
        <Icon icon="share" />
        Share
      </Button>
    );
  })();

  // The star for the folder you are *in*, drawn on the page heading beside the
  // folder's name -- the breadcrumb above it is a trail to here, while the
  // heading *is* the folder. Absent at the Library root, which is a view rather
  // than a folder and so has nothing to star.
  const folderStar =
    folderId === null || folderId === undefined || isTopLevel ? undefined : (
      <FavoriteToggleButton
        type={ResourceType.Folder}
        id={folderId}
        // No margin and no `align-middle`: this is handed to the heading's
        // `adornment` slot, which is a flex row, so the spacing is its `gap`
        // and `align-middle` was inert. Both were left from when the star
        // really did flow inline after the last word of the name.
        className="h-7 w-7"
        iconClassName="h-4 w-4"
      />
    );

  const handleTypeFilterChange = useCallback(
    (value: string) => {
      if (isTypeFilter(value)) {
        listPageModel.setTypeFilter(value);
      }
    },
    [listPageModel]
  );

  const handleOwnershipFilterChange = useCallback(
    (value: string) => {
      if (isOwnershipFilter(value)) {
        listPageModel.setOwnership(value);
      }
    },
    [listPageModel]
  );

  const handleClearFilters = useCallback(() => {
    listPageModel.clearFilters();
  }, [listPageModel]);

  const handleRetry = useCallback(() => {
    listPageModel.retry();
  }, [listPageModel]);

  // The header's two shapes. See `ListFilterMenu`.
  const isNarrow = useIsBelowSm();

  const handleViewModeChange = useCallback(
    (value: LibraryViewMode) => {
      listPageModel.setViewMode(value);
    },
    [listPageModel]
  );

  // Below `sm` the band collapses into one menu that rides the title's row —
  // see `ListFilterMenu`. Both forms are driven by the same handlers; only the
  // shape changes.
  const filterMenu = (
    <>
      <LibraryFiltersMenu
        typeFilter={typeFilter}
        onTypeFilterChange={handleTypeFilterChange}
        ownershipFilter={ownershipFilter}
        onOwnershipFilterChange={handleOwnershipFilterChange}
        showOwnership={isTopLevel}
        filtersActive={filtersActive}
        onClearFilters={handleClearFilters}
      />
      <LibraryViewToggle
        viewMode={view}
        onViewModeChange={handleViewModeChange}
      />
    </>
  );

  const filterControl = (
    <LibraryFilters
      typeFilter={typeFilter}
      onTypeFilterChange={handleTypeFilterChange}
      ownershipFilter={ownershipFilter}
      onOwnershipFilterChange={handleOwnershipFilterChange}
      showOwnership={isTopLevel}
      filtersActive={filtersActive}
      onClearFilters={handleClearFilters}
      viewMode={view}
      onViewModeChange={handleViewModeChange}
    />
  );

  const baseItems = isTopLevel ? topLevelItems : nestedFolderItems;

  const nestedChildrenByFolderId = useMemo(
    () =>
      buildExpandedChildrenListItems({
        childrenByFolderId,
        chatSummaryByFolderId,
      }),
    [childrenByFolderId, chatSummaryByFolderId]
  );

  // Splice expanded folders' children in place. This is what makes `All`
  // complete: the Library root returns root folders plus *unfiled* artifacts, so
  // anything filed into a folder was simply absent from the view.
  const { items: listItems, metaById: treeMetaById } = useMemo(
    () =>
      treeEnabled
        ? flattenLibraryTree({
            rootItems: baseItems,
            expandedIds,
            childrenByFolderId: nestedChildrenByFolderId,
            loadingFolderIds,
          })
        : { items: baseItems, metaById: new Map<string, LibraryTreeMeta>() },
    [
      treeEnabled,
      baseItems,
      expandedIds,
      nestedChildrenByFolderId,
      loadingFolderIds,
    ]
  );

  const itemTreeMeta = useCallback(
    (item: FolderContentsListItem): LibraryTreeMeta | undefined =>
      treeMetaById.get(getListItemId(item)),
    [treeMetaById]
  );

  return (
    <ResourcePageShell breadcrumbs={breadcrumbs} actions={folderShareAction}>
      {/* Page gutters live here, not in `ResourceListView` — `containerClassName`
          drops its default `px-8` so the two don't stack. 1640px keeps very wide
          displays from stretching rows to an unreadable line length, while still
          being far wider than the 896px cap this page used to have. */}
      <div className="mx-auto flex min-h-full w-full max-w-[1640px] flex-col px-5 pb-16 pt-1 sm:px-8 sm:pt-5 lg:px-12">
        <ResourceListView<FolderContentsListItem>
          headerSize="compact"
          containerClassName="px-0"
          title={title}
          titleLoading={titleLoading}
          titleAdornment={isTopLevel ? suggestFoldersAction : folderStar}
          noun="item"
          items={listItems}
          // The server total, not the loaded slice: with a page size of 100 a
          // 350-item library reported "100 items" until you scrolled.
          totalCount={pinnedTotal}
          status={status}
          errorText={loadError ?? undefined}
          onRetry={handleRetry}
          // A refresh over existing rows (filter switch, sort, search) leaves the
          // previous page on screen; mark it stale rather than let it swap
          // silently when the new one lands.
          isRefetching={isFetching && hasLoadedOnce}
          // One of the two, never both: `isNarrow` picks the shape, and the
          // band's own row disappears entirely on a phone.
          {...(isNarrow
            ? { toolbarExtras: filterMenu }
            : { headerExtras: filterControl })}
          layoutMode={view}
          renderCard={renderCard}
          itemTreeMeta={treeEnabled ? itemTreeMeta : undefined}
          onToggleExpand={treeEnabled ? handleToggleExpand : undefined}
          filterFn={filterFn}
          renderItem={renderItem}
          icon={Folder}
          renderIcon={renderIcon}
          keyFn={getListItemId}
          onClickItem={handleClickItem}
          onFocus={handleFocusItems}
          itemActions={getItemActions}
          alwaysShowSelection
          selectionActions={() =>
            shareMoveArchiveItems({
              onShare: () => void shareAction.execute({}),
              onMoveToFolder: () => void moveToFolderAction.execute({}),
              onArchive: () => void archiveResourceAction.execute({}),
            })
          }
          onOpenActionMenu={handleOpenActionMenu}
          onDropOnItem={handleDropOnFolder}
          canDropOnItem={canDropOnFolder}
          canDragItem={canDragLibraryItem}
          renderDragImage={renderDragCard}
          emptyIcon={
            <FolderOpen className="h-12 w-12 text-muted-foreground/50 mb-4" />
          }
          emptyText={emptyText}
          noMatchText={`No ${typeNoun} match your filters`}
          onClearFilters={handleClearFilters}
          filtersActive={filtersActive}
          searchPlaceholder="Search..."
          searchQuery={searchDraft}
          onSearchQueryChange={handleSearchChange}
          sortOptions={sortOptionsForType(typeFilter)}
          defaultSort="updatedAt"
          activeSort={sort ?? 'updatedAt'}
          sortDirection={sortDir}
          onSortChange={handleSortChange}
          columns={folderListColumns}
          hasMore={hasMore}
          onLoadMore={handleLoadMore}
          isLoadingMore={isLoadingMore && listItems.length > 0}
          primaryAction={primaryAction}
        />
      </div>
    </ResourcePageShell>
  );
};

const FolderContentsPageShell: React.FC<{ folderId: string | null }> = ({
  folderId,
}) => {
  const client = useTenantedClient();
  const queryClient = useQueryClient();
  const orgId = useWhoami((s) => s.org.id);
  const urlAdapter = useUrlAdapter();
  const watchablesFactory = useWatchablesFactory();

  const createModels = useCallback(
    () => [
      new FolderContentsPageModel({
        orgId,
        queryClient,
        watchablesFactory,
        urlAdapter,
        initialSearchParams: new URLSearchParams(
          typeof window !== 'undefined' ? window.location.search : ''
        ),
        client,
        folderId,
      }),
      new ResourceSelectionModel(),
    ],
    [urlAdapter, client, queryClient, orgId, folderId, watchablesFactory]
  );

  return (
    <div className="h-full w-full bg-background">
      <ModelScopeFactory
        createModels={createModels}
        deps={[
          urlAdapter,
          client,
          queryClient,
          orgId,
          folderId,
          watchablesFactory,
        ]}
      >
        <FolderContentsListContent key={folderId ?? 'root'} />
      </ModelScopeFactory>
    </div>
  );
};

export const FolderContentsPage: React.FC = () => {
  const { folderId } = useParams<{ folderId?: string }>();
  return <FolderContentsPageShell folderId={folderId ?? null} />;
};

/** Folder contents for a known folder id (e.g. artifact panel). Uses the app Router; do not wrap in MemoryRouter. */
export const FolderContentsPageArtifactView: React.FC<{
  folderId: string;
}> = ({ folderId }) => (
  <FolderArtifactFolderIdProvider folderId={folderId}>
    <FolderContentsPageShell folderId={folderId} />
  </FolderArtifactFolderIdProvider>
);

/**
 * The top-level Library (no folder id) for rendering as a panel artifact, so a
 * "Library" breadcrumb can open it in place instead of routing the whole app to
 * `/folders`. Uses the app Router; do not wrap in MemoryRouter.
 */
export const LibraryContentsPageArtifactView: React.FC = () => (
  <FolderContentsPageShell folderId={null} />
);
